import { useData } from 'vike-react/useData';
import { useState } from 'react';
import {
  ArrowRight,
  Award,
  CheckCircle2,
  FileSearch,
  Lock,
  ScrollText,
  ShieldCheck,
  TrendingUp,
  Users,
} from 'lucide-react';
import type { Data } from './+data';
import { Container } from '../../components/layout/Container';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { FilterPill } from '../../components/ui/filter-pill';
import { PartnersMarquee } from '../../components/home/PartnersMarquee';
import { Reveal } from '../../components/motion/Reveal';
import { CountUp } from '../../components/motion/CountUp';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';

const GLOBAL_STATS = [
  {
    value: 7,
    suffix: '',
    label: 'Profils accompagnés (particulier, pro, entreprise, collectivité…)',
    icon: Users,
  },
  {
    value: 3,
    suffix: '',
    label: "Pôles d'expertise : rénovation, courtage énergie, mandat CEE",
    icon: Award,
  },
  {
    value: 100,
    suffix: ' %',
    label: 'Tous nos dossiers sont réels, signés et documentés. Engagement DGCCRF.',
    icon: ShieldCheck,
  },
];

const AUDIENCE_FILTERS = [
  { id: '', label: 'Tous' },
  { id: 'entreprises', label: 'Entreprises' },
  { id: 'artisans', label: 'Artisans' },
  { id: 'collectivites', label: 'Collectivités' },
  { id: 'coproprietes', label: 'Copropriétés' },
  { id: 'agriculteurs', label: 'Agriculteurs' },
];

const METHODOLOGIE = [
  {
    icon: FileSearch,
    title: 'Sourcing du dossier',
    text: 'On choisit un dossier représentatif du segment, avec des résultats vérifiables (factures, attestations, audit après travaux).',
  },
  {
    icon: Lock,
    title: 'Validation client',
    text: "Aucun cas n'est publié sans l'autorisation écrite et signée du client concerné. C'est non négociable.",
  },
  {
    icon: ScrollText,
    title: 'Documentation publique',
    text: 'Chaque cas indique le périmètre, les dispositifs mobilisés, les chiffres clés et les sources réglementaires.',
  },
];

const SCENARIOS_TYPES = [
  {
    segment: 'Particulier',
    title: 'Maison F (Pays de la Loire) — bouquet PAC + isolation combles',
    summary:
      "Remplacement d'une chaudière fioul par une PAC air-eau, isolation des combles perdus. Cumul MaPrimeRénov’ modeste + Prime énergie CEE + éco-PTZ.",
    kpiLabel: 'Reste à charge',
    kpiBefore: 21500,
    kpiAfter: 4200,
  },
  {
    segment: 'Tertiaire',
    title: 'Immeuble bureaux 6 200 m² — GTB classe A + relamping',
    summary:
      "Mise en place d'une GTB classe A (fiche BAT-TH-116, Coup de pouce actif) et relamping LED. Trajectoire OPERAT \u221240 % atteignable à 2030.",
    kpiLabel: 'Économies / an',
    kpiBefore: 0,
    kpiAfter: 87000,
  },
  {
    segment: 'Copropriété',
    title: 'Copropriété 38 lots — ITE + chaudière collective biomasse',
    summary:
      'Vote en 1re convocation avec MaPrimeRénov’ Copro + CEE collectifs + éco-PTZ collectif. Gain DPE : E \u2192 C.',
    kpiLabel: 'Reste à charge par lot',
    kpiBefore: 18500,
    kpiAfter: 6900,
  },
];

export default function CasClientsIndex() {
  const { cases } = useData<Data>();
  const [audienceFilter, setAudienceFilter] = useState('');

  const filtered = cases.filter(
    (c) => !audienceFilter || (c as { segment?: string }).segment === audienceFilter,
  );

  const hasNoCases = cases.length === 0;

  return (
    <div className="bg-white">
      {/* ── Hero ── */}
      <div className="relative overflow-hidden border-b border-stone-200 bg-stone-50/60 py-14 md:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-32 h-[360px] w-[360px] rounded-full bg-brand-100/50 blur-[110px]"
        />
        <Container className="relative">
          <Reveal>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 ring-1 ring-inset ring-brand-200/60">
                <Award className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
              </span>
              <p className="eyebrow">Résultats concrets</p>
            </div>
            <h1 className="h-hero mt-4">
              Des dossiers vérifiables,{' '}
              <span className="text-gradient">des résultats chiffrés.</span>
            </h1>
            <p className="mt-4 max-w-2xl text-[16px] leading-relaxed text-stone-600">
              Des dossiers réels, signés et documentés. Aucun témoignage fictif&nbsp;: c'est notre
              engagement DGCCRF, et on vous explique comment on choisit ce qu'on publie.
            </p>
          </Reveal>

          {/* ── KPI strip ── */}
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {GLOBAL_STATS.map(({ value, suffix, label, icon: Icon }) => (
              <div
                key={label}
                className="rounded-xl border border-stone-200 bg-white p-5 shadow-subtle"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50">
                    <Icon className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-display text-2xl font-bold text-ink">
                      <CountUp to={value} duration={1.4} suffix={suffix} />
                    </p>
                    <p className="text-[12px] text-stone-500">{label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </div>

      <Container className="py-14">
        {/* ── Audience filters ── */}
        <div className="mb-8 flex flex-wrap gap-2">
          {AUDIENCE_FILTERS.map((f) => (
            <FilterPill
              key={f.id}
              tone="ink"
              active={audienceFilter === f.id}
              onClick={() => setAudienceFilter(f.id)}
            >
              {f.label}
            </FilterPill>
          ))}
        </div>

        {filtered.length === 0 ? (
          <>
            {/* ── État vide : bandeau d'info + méthodologie + scénarios types ── */}
            <div className="mx-auto max-w-3xl rounded-2xl border border-brand-200/70 bg-gradient-to-br from-brand-50/70 via-paper to-accent-50/40 p-6 md:p-8">
              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-paper shadow-soft ring-1 ring-brand-200">
                  <ShieldCheck className="h-5 w-5 text-brand-700" strokeWidth={1.7} />
                </span>
                <div>
                  <p className="eyebrow">Notre engagement DGCCRF</p>
                  <h2 className="mt-2 font-display text-[20px] font-bold leading-tight text-ink md:text-xl">
                    On ne publie que des cas autorisés par écrit
                  </h2>
                  <p className="mt-2 text-[14.5px] leading-relaxed text-stone-600">
                    {hasNoCases
                      ? "Plusieurs dossiers sont en cours d'autorisation de publication. En attendant, voici notre méthodologie et trois scénarios types qui décrivent ce qu'on rencontre habituellement."
                      : 'Aucun cas client ne correspond à ce filtre pour le moment. En attendant, on partage notre méthodologie et trois scénarios types.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Méthodologie de publication */}
            <div className="mt-12">
              <div className="mx-auto max-w-2xl text-center">
                <p className="eyebrow justify-center">Méthodologie de publication</p>
                <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink">
                  Comment on choisit ce qu'on publie
                </h2>
                <p className="mt-3 text-[14.5px] leading-relaxed text-stone-600">
                  Trois étapes systématiques avant qu'un dossier apparaisse ici.
                </p>
              </div>
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {METHODOLOGIE.map(({ icon: Icon, title, text }, idx) => (
                  <Reveal key={title} delay={idx * 0.08}>
                    <div className="h-full rounded-2xl border border-stone-200 bg-paper p-5 shadow-soft">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                        <Icon className="h-4 w-4" strokeWidth={1.7} />
                      </span>
                      <p className="mt-3 font-display text-[15px] font-semibold text-ink">
                        {title}
                      </p>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-600">{text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Scénarios types */}
            <div className="mt-14">
              <div className="mx-auto max-w-2xl text-center">
                <p className="eyebrow justify-center">Scénarios types</p>
                <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink">
                  Ce qu'on rencontre habituellement
                </h2>
                <p className="mt-3 text-[14.5px] leading-relaxed text-stone-600">
                  Profils représentatifs, chiffres indicatifs basés sur les barèmes en vigueur. Ne
                  se substituent pas à un diagnostic personnalisé.
                </p>
              </div>
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {SCENARIOS_TYPES.map((s, idx) => (
                  <Reveal key={s.title} delay={idx * 0.06}>
                    <div className="flex h-full flex-col rounded-xl border border-stone-200 bg-paper p-6 shadow-soft">
                      <Badge variant="secondary" className="mb-3 w-fit">
                        {s.segment} · scénario type
                      </Badge>
                      <h3 className="font-display text-[16px] font-semibold leading-snug text-ink">
                        {s.title}
                      </h3>
                      <p className="mt-2 line-clamp-4 text-[13px] leading-relaxed text-stone-500">
                        {s.summary}
                      </p>
                      <div className="mt-4 flex items-end gap-3">
                        <div className="flex-1 rounded-lg bg-stone-50 px-3 py-2">
                          <p className="text-[10px] text-stone-400">{s.kpiLabel} — avant</p>
                          <p className="font-display text-lg font-bold tabular-nums text-stone-500">
                            {s.kpiBefore.toLocaleString('fr-FR')} €
                          </p>
                        </div>
                        <div className="pb-2 text-stone-300">→</div>
                        <div className="flex-1 rounded-lg bg-brand-50 px-3 py-2">
                          <p className="text-[10px] text-brand-600">{s.kpiLabel} — après</p>
                          <p className="font-display text-lg font-bold tabular-nums text-brand-700">
                            {s.kpiAfter.toLocaleString('fr-FR')} €
                          </p>
                        </div>
                      </div>
                      <p className="mt-3 inline-flex items-center gap-1 text-[11.5px] text-stone-400">
                        <CheckCircle2 className="h-3 w-3 text-stone-300" strokeWidth={2.2} />
                        Scénario type · ne constitue pas un engagement contractuel
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c, i) => {
              const cs = c as typeof c & {
                kpiBefore?: number;
                kpiAfter?: number;
                kpiLabel?: string;
                segment?: string;
              };
              return (
                <Reveal key={c.id} delay={i * 0.06}>
                  <a
                    href={`/cas-clients/${c.slug}`}
                    className="group flex h-full flex-col rounded-xl border border-stone-200 bg-white p-6 shadow-subtle transition-all hover:border-brand-200 hover:shadow-medium"
                  >
                    {cs.segment && (
                      <Badge variant="secondary" className="mb-3 w-fit capitalize">
                        {cs.segment}
                      </Badge>
                    )}
                    <h2 className="font-display text-[17px] font-semibold leading-snug text-ink group-hover:text-brand-700">
                      {c.title}
                    </h2>
                    {c.summary && (
                      <p className="mt-2 line-clamp-3 text-[13px] leading-relaxed text-stone-500">
                        {c.summary}
                      </p>
                    )}

                    {cs.kpiBefore != null && cs.kpiAfter != null && (
                      <div className="mt-4 flex gap-3">
                        <div className="flex-1 rounded-lg bg-stone-50 px-3 py-2">
                          <p className="text-[10px] text-stone-400">Avant</p>
                          <p className="font-display text-lg font-bold text-stone-500">
                            {cs.kpiBefore.toLocaleString('fr-FR')}
                          </p>
                        </div>
                        <div className="flex items-center text-stone-300">→</div>
                        <div className="flex-1 rounded-lg bg-brand-50 px-3 py-2">
                          <p className="text-[10px] text-brand-600">Après</p>
                          <p className="font-display text-lg font-bold text-brand-700">
                            {cs.kpiAfter.toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="mt-auto flex items-center justify-end pt-4">
                      <span className="flex items-center gap-1 text-[13px] font-semibold text-brand-700">
                        Voir le cas
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </a>
                </Reveal>
              );
            })}
          </div>
        )}

        {/* ── CTA ── */}
        <div className="mt-16 rounded-2xl border border-stone-200 bg-stone-50/60 p-8 text-center">
          <h2 className="font-display text-xl font-bold text-ink">
            Votre dossier pourrait être ici
          </h2>
          <p className="mt-2 text-[14px] text-stone-600">
            Démarrez un diagnostic gratuit pour estimer vos primes et voir si on peut vous
            accompagner.
          </p>
          <Button asChild variant="primary" size="xl" className="mt-5">
            <a href="/outils">
              Démarrer le diagnostic
              <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-[11.5px] text-stone-400">
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-brand-500" strokeWidth={2.2} />
              60 secondes
            </span>
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-brand-500" strokeWidth={2.2} />
              Sans inscription
            </span>
            <span className="inline-flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-brand-500" strokeWidth={2.2} />
              Chiffrage immédiat
            </span>
          </div>
        </div>
      </Container>

      {/* ── Preuve sociale : fournisseurs avec qui on travaille ── */}
      <PartnersMarquee />

      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Cas clients', url: 'https://solutionecofrance.fr/cas-clients' },
        ]}
      />
    </div>
  );
}
